// src/hooks/useFaviconBadge.ts
import { useEffect } from 'react';

/**
 * Draws a small red badge with a number (or a dot when count > 99)
 * onto the page's favicon. Restores the original icon when count is 0.
 */
export function useFaviconBadge(count: number) {
  useEffect(() => {
    // Capture the current favicon href once so we can restore it later.
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']")
    );

    if (links.length === 0) {
      // No favicon on the page — nothing we can do.
      return;
    }

    const originalHref = links[0].href;

    // Helper: create a data-URL favicon with a badge
    const buildBadgeFavicon = (
      baseHref: string,
      n: number
    ): Promise<string> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const size = 64; // standard favicon-ish canvas
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('canvas 2d unavailable'));

          // Draw base icon
          ctx.drawImage(img, 0, 0, size, size);

          // Badge geometry
          const isDot = n > 99;
          const badgeR = isDot ? 14 : 20;
          const badgeCX = size - badgeR;
          const badgeCY = badgeR;

          // Red circle
          ctx.beginPath();
          ctx.arc(badgeCX, badgeCY, badgeR, 0, Math.PI * 2);
          ctx.fillStyle = '#EF4444';
          ctx.fill();

          // White border for contrast
          ctx.lineWidth = 4;
          ctx.strokeStyle = '#FFFFFF';
          ctx.stroke();

          // Count text (unless dot mode)
          if (!isDot) {
            ctx.fillStyle = '#FFFFFF';
            ctx.font = `bold ${badgeR + 4}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const label = n > 9 ? '9+' : String(n);
            ctx.fillText(label, badgeCX, badgeCY + 1);
          }

          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => reject(new Error('favicon load failed'));
        img.src = baseHref;
      });

    let cancelled = false;

    (async () => {
      try {
        if (count <= 0) {
          // Restore original
          links.forEach((l) => (l.href = originalHref));
          return;
        }

        const newHref = await buildBadgeFavicon(originalHref, count);
        if (cancelled) return;

        // Apply to every icon link (some pages have multiple sizes)
        links.forEach((l) => (l.href = newHref));
      } catch (err) {
        // Non-fatal — badging is a nice-to-have
        console.warn('Favicon badge failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [count]);
}